/**
 * What happens when a wall gives out.
 *
 * Damage takes lines to zero; this is the part after that. A wall at zero is not
 * a wall — it is gone — and what is left is a different ship.
 *
 * ── Destroyed means removed ─────────────────────────────────────────────────
 *
 * Taken out of the graph outright, along with any joint left holding nothing and
 * any part that was inside a room that no longer exists. There is no such thing
 * as a broken line: a line is on the ship or it is not.
 *
 * That is worth stating because the first attempt did the opposite — kept
 * destroyed walls at zero health and taught every other piece of code to ignore
 * them. It meant two versions of the same ship in one object, and every question
 * had to say which one it meant: compartment tracing wanted the standing walls,
 * the hull reading wanted all of them, collision targeting wanted the standing
 * ones again, and the renderer had to be told which pieces to stop drawing.
 * Every one of those was a place to get it wrong, and most of them were.
 *
 * The record of what the ship *should* be is the blueprint, which is a separate
 * graph and always has been. That is where a repair builds back from, and it is
 * what the hull reading is measured against — see `hullIntegrity`.
 *
 * ── The cascade ─────────────────────────────────────────────────────────────
 *
 * Opening a compartment does not only cost you that wall. The other walls of
 * that compartment were holding each other up; the ones that are not also
 * holding up a *different* compartment have nothing left to do, and they go too.
 *
 * That is the rule as stated, and it is deliberately unkind: one wall lost to a
 * scrape is a repair, and one wall lost from a compartment that was the last
 * thing bracing four others is a ship coming apart. It also means a compartment
 * shared with the rest of the ship is far more robust than a bubble hung off the
 * side, which is a structural fact the editor can teach without saying anything.
 *
 * Run to a fixed point, because a cascade can open the compartment next door.
 *
 * ── Components go with their compartment, or with their wall ────────────────
 *
 * A part sitting in a space that no longer exists is not on the ship any more.
 * Neither is one bolted to a wall that is no longer there: an edge part — a
 * thruster, a turret, a collector — mounts *on* a line rather than inside a
 * room, so the line going is the same event for it that a room collapsing is
 * for everything else.
 *
 * Without that second rule a destroyed stern left both engines hanging in the
 * space where it used to be, still drawn, still listed in the hold's worth, and
 * silently doing nothing — because `activeComponents` correctly refuses to count
 * a thruster with nothing to push against. Dormant and invisible is the worst of
 * both: the ship reads as intact and flies like a wreck. Shedding them makes the
 * loss the thing you can see.
 *
 * Either way, some survive the experience and drift; the rest are scrap. That is
 * a coin flip, seeded so a given wreck always comes apart the same way.
 */
import { findCompartments } from '../geometry/compartments.js';
import { COMPONENT_SPECS, thrusterSet } from '../components.js';
import { mountLineFor } from './placement.js';
/**
 * Drop anything no longer holding on to the ship.
 *
 * Joints go with the last wall that used to reach them. They are what the
 * collision outline is traced from, so leaving them behind would keep a hull
 * exactly its original size however much of it had come off — a ship with its
 * nose torn away would go on colliding with things using the nose.
 */
function pruned(hull) {
    const attached = new Set();
    for (const line of hull.lines) {
        attached.add(line.a);
        attached.add(line.b);
    }
    return { ...hull, joints: hull.joints.filter((joint) => attached.has(joint.id)) };
}
/** Whether a point is inside a polygon, by the crossing rule. */
function inside(polygon, x, y) {
    let within = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        if (a.y > y === b.y > y)
            continue;
        if (x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x)
            within = !within;
    }
    return within;
}
/** A compartment's outline, from the joints it is traced through. */
function outlineOf(compartment, joints) {
    const points = [];
    for (const id of compartment.jointIds) {
        const joint = joints.get(id);
        if (joint)
            points.push({ x: joint.x, y: joint.y });
    }
    return points;
}
/**
 * Which compartment a part sits in, or null if it sits in none.
 *
 * A part outside every compartment is on a strut or hanging off the edge, and it
 * has nothing to lose when a room collapses.
 */
export function compartmentOf(graph, component, compartments = findCompartments(graph)) {
    const joints = new Map(graph.joints.map((joint) => [joint.id, joint]));
    for (const compartment of compartments) {
        if (inside(outlineOf(compartment, joints), component.position.x, component.position.y)) {
            return compartment.id;
        }
    }
    return null;
}
/**
 * Settle a hull after damage: break what has given out, and everything that was
 * relying on it.
 *
 * Idempotent on a hull that has already settled — nothing at zero that is still
 * bracing something, no parts adrift — so it is safe to call every time damage
 * lands rather than only when something looks wrong.
 */
export function collapse(hull, random) {
    /*
     * The rooms this ship has right now, before anything is taken away.
     *
     * A wall only gives way because the room it was bracing has opened, so the
     * question is always "what did this wall used to hold up" — and that has to be
     * asked of the ship as it stands at the start of the step.
     */
    const before = findCompartments(hull);
    const wasBracing = new Set();
    for (const room of before)
        for (const id of room.lineIds)
            wasBracing.add(id);
    /** Where each part lived before anything came apart. */
    const homes = new Map(hull.components.map((component) => [component.id, compartmentOf(hull, component, before)]));
    /**
     * And which wall each edge part was bolted to, asked of the ship as it stood.
     *
     * Same reasoning as `homes`: once a line is gone there is nothing left to ask,
     * so the answer has to be taken before the cascade runs. A part that was on
     * nothing to begin with maps to null and is left alone — that is an illegal
     * placement the editor already refuses to launch, not a wall that failed.
     */
    const mounts = new Map(hull.components
        .filter((component) => COMPONENT_SPECS[component.type].mounting === 'edge')
        .map((component) => [component.id, mountLineFor(component, hull)?.id ?? null]));
    const broke = [];
    // Walls that have taken all they can. These go first; the cascade follows.
    let lines = hull.lines.filter((line) => {
        if (line.health > 0)
            return true;
        broke.push(line.id);
        return false;
    });
    /*
     * To a fixed point.
     *
     * One collapse opens the compartment next door, which strands its walls in
     * turn. Bounded by the line count because every pass removes at least one wall
     * or stops.
     */
    for (let pass = 0; pass <= hull.lines.length; pass++) {
        const rooms = findCompartments({ ...hull, lines });
        const bracing = new Set();
        for (const room of rooms)
            for (const id of room.lineIds)
                bracing.add(id);
        // A wall goes when it used to brace a compartment and the compartment it
        // braced is gone. Struts that never enclosed anything are left alone — they
        // were holding nothing up to begin with.
        const giving = lines.filter((line) => wasBracing.has(line.id) && !bracing.has(line.id));
        if (giving.length === 0)
            break;
        const gone = new Set(giving.map((line) => line.id));
        for (const id of gone)
            broke.push(id);
        lines = lines.filter((line) => !gone.has(line.id));
    }
    const settled = pruned({ ...hull, lines });
    const stillStanding = new Set(findCompartments(settled).map((room) => room.id));
    const standing = new Set(settled.lines.map((line) => line.id));
    const lost = [];
    const kept = [];
    for (const component of hull.components) {
        const home = homes.get(component.id) ?? null;
        const mount = mounts.get(component.id);
        // Bolted to a wall that has gone. Checked first because an edge part
        // usually has no room to lose, so the test below would keep it forever.
        if (mount != null && !standing.has(mount)) {
            lost.push({ component, fate: random.float() < 0.5 ? 'dropped' : 'broken' });
            continue;
        }
        // No room to lose, or the room is still there.
        if (home === null || stillStanding.has(home)) {
            kept.push(component);
            continue;
        }
        lost.push({ component, fate: random.float() < 0.5 ? 'dropped' : 'broken' });
    }
    /*
     * A step in which nothing gave way returns the hull it was given.
     *
     * Identity, not just equality. `{ ...settled, components: kept }` builds a new
     * components array every call, and the flight loop memoises the ship's
     * performance on exactly that array -- so a hull that had not changed still
     * minted a new `performance`, which is a dependency of the effect that owns
     * the scene. The whole scene was torn down and rebuilt.
     *
     * That is invisible until something calls this every frame. Collisions did
     * not: they are rare and have immunity frames. Heat does -- it is an
     * environment rather than a contact, and ticking is the point of it -- so
     * sitting near a star rebuilt the world sixty times a second and took pointer
     * lock with it each time, which reads as being thrown out of the controls.
     *
     * `pruned` is still allowed to matter: a hull can arrive with joints that
     * nothing attaches to, and dropping them changes the collision outline. So the
     * short circuit asks whether it actually removed any, rather than assuming a
     * step with no breakages changed nothing.
     */
    const idle = broke.length === 0 && lost.length === 0 && settled.joints.length === hull.joints.length;
    if (idle) {
        return {
            hull,
            broke,
            lost,
            destroyed: !canStillFly(hull.components.map((component) => component.type)),
        };
    }
    return {
        hull: { ...settled, components: kept },
        broke,
        lost,
        destroyed: !canStillFly(kept.map((component) => component.type)),
    };
}
/**
 * Whether what is left of a ship is still a ship.
 *
 * A seat to fly it from, an engine to power it, and *both* kinds of thruster:
 * something to move with and something to place it with. Losing either set
 * leaves a hull that can technically still be pushed around and cannot be
 * flown — no precision means no landing, and no main means nothing to land at.
 *
 * Deliberately stricter than `meetsRequirements`, which the editor uses to
 * decide whether a design is legal and which accepts any thruster at all. The
 * two are asking different questions: one is "may this be built", the other is
 * "is this still flying", and the second is the one that ends a run. Kept apart
 * so that tightening this cannot quietly invalidate a design somebody already
 * owns.
 */
export function canStillFly(componentTypes) {
    const has = (type) => componentTypes.includes(type);
    const sets = new Set(componentTypes.map(thrusterSet).filter(Boolean));
    return has('pilotSeat') && has('engine') && sets.has('precision') && sets.has('main');
}
//# sourceMappingURL=structure.js.map